import React, { useState, useEffect, useRef, useCallback } from 'react';
import Notify from 'notifyjs';
import { Fetcher, getParams, HTTPError, appendScript } from '@this/src/util';
import type { RouteComponentProps } from 'react-router-dom';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import Trip, { convertTripResponseToArgs } from '@this/domain/trip/trip2';
import Order, { convertOrderResponseToArgs } from '@this/domain/order2';
import ShareholderTicketList from '@this/domain/shareholder_ticket_list/index';
import type { BulkTicketArgs } from '@this/domain/bulk_ticket2';
import BulkTicket from '@this/domain/bulk_ticket2';
import type { TaxTypeArgs } from '@this/domain/tax_type';
import TaxType from '@this/domain/tax_type';
import type { SuppliedItemResponseArgs } from '@this/domain/supplied_item/supplied_item2';
import SuppliedItem, { convertSuppliedItemResponseToArgs } from '@this/domain/supplied_item/supplied_item2';
import PaymentMethodList from '@this/domain/payment_method/payment_method_list';

import type { Message } from '@this/components/trips_management/trips/trips_chat/types';
import type { TicketOprResponseArgs } from '@this/domain/ticket_opr_response';
import type { MessageTemplate, OrderItemStatusLogInfo } from '@this/components/arrangement/virtual_counter/types';
import type { PaymentMethodArgs } from '@this/domain/payment_method/payment_method';
import type { CabinOption } from '@this/domain/cabin_option';
import type { ShareholderInfo } from '@this/domain/other_service_shareholder_info';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import type { IndividualDomesticAirSuppliedItem } from '@this/domain/individual_domestic_air_supplied_item';
import { throttle } from 'lodash';
import { observer } from 'mobx-react';
import { VirtualCounterHeader } from './virtual_counter_header';
import { VirtualCounterBody } from './virtual_counter_body';
import { VirtualCounterAside } from './virtual_counter_aside';
import { HorizontalResizableContainer } from './horizontal_resizable_container';
import { VirtualCounterProvider } from './virtual_counter_context';

type Props = RouteComponentProps;

type VirtualCounterResponse = {
  signed_in: {
    signed_in: boolean;
    arranger: {
      id: number;
      email: string;
      first_name: string;
      last_name: string;
      created_at: string;
      updated_at: string;
      service_id: string;
      deleted_at: string | null;
    };
  };
  pusherKey: string;
  pusherChannelPrefix: string;
};

type MessageResponse = {
  success: boolean;
  receiptIssuable: boolean;
  messages: Message[];
  order_item_status_log_info: OrderItemStatusLogInfo;
};
type TripResponse = {
  trip: TripResponseArgs;
  organization_plan: string;
  ticket_opr_response_price: TicketOprResponseArgs[] | null;
  hotel_element_provider_options: { [key: string]: string };
  shareholder_tickets: any[];
  message_templates: MessageTemplate[];
  bulk_tickets: BulkTicketArgs[];
  shipping_category_options: {
    yamato: string;
    sagawa: string;
    letter_pack: string;
    ecohai: string;
  };
  wifi_provider_options: {
    vision: string;
  };
  car_type_options: [string, string][];
  car_provider_options: [string, string][];
  fp_reservation_json: object | null;
  mynavi_reservation_json: object | null;
  payment_methods: PaymentMethodArgs[];
  shareholder_infos: ShareholderInfo[];
  cabin_options: CabinOption[];
  individual_target_supplied_items: IndividualTargetSuppliedItem[];
  individual_domestic_air_supplied_items: IndividualDomesticAirSuppliedItem[];
  tax_types: TaxTypeArgs[];
};

const VirtualCounter: React.FC<Props> = () => {
  const params = getParams();

  // Refs for Pusher
  const pusherRef = useRef<any>(null);
  const eventChannelRef = useRef<any>(null);
  const tripChannelRef = useRef<any>(null);
  const timeoutIdsRef = useRef<number[]>([]);

  // State
  const [initialTripId] = useState<string | null>(params.trip_id ?? null);
  const [pusherKey, setPusherKey] = useState<string | null>(null);
  const [pusherChannelPrefix, setPusherChannelPrefix] = useState<string | null>(null);
  const [suppliedItems, setSuppliedItems] = useState<SuppliedItem[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const selectedTripRef = useRef<Trip | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [orderItemStatusLogInfo, setOrderItemStatusLogInfo] = useState<OrderItemStatusLogInfo | null>(null);
  const [tripNotFindMessage, setTripNotFindMessage] = useState('');
  const [ticketOprResponse, setTicketOprResponse] = useState<TicketOprResponseArgs[]>([]);
  const [planName, setPlanName] = useState('');
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [loadingSelectedTrip, setLoadingSelectedTrip] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hotelElementProviderOptions, setHotelElementProviderOptions] = useState<{ [key: string]: string }>({});
  const [serviceId, setServiceId] = useState(1);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [bulkTickets, setBulkTickets] = useState<BulkTicket[]>([]);
  const [shareholderTickets, setShareholderTickets] = useState(new ShareholderTicketList([]));
  const [paymentMethods, setPaymentMethods] = useState(new PaymentMethodList([]));
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [shareholderInfos, setShareholderInfos] = useState<ShareholderInfo[]>([]);
  const [cabinOptions, setCabinOptions] = useState<CabinOption[]>([]);
  const [individualTargetSuppliedItems, setIndividualTargetSuppliedItems] = useState<
    IndividualTargetSuppliedItem[]
  >([]);
  const [individualDomesticAirSuppliedItems, setIndividualDomesticAirSuppliedItems] = useState<
    IndividualDomesticAirSuppliedItem[]
  >([]);
  const [reservationJson, setReservationJson] = useState<{ fp: object | null; mynavi: object | null }>({
    fp: null,
    mynavi: null
  });
  const pusherKeyRef = useRef<string | null>(null);
  const pusherChannelPrefixRef = useRef<string | null>(null);

  const createTimeout = useCallback(() => {
    timeoutIdsRef.current.forEach(window.clearTimeout);
    timeoutIdsRef.current = [];
  }, []);

  const showDesktopNotification = useCallback((body: string) => {
    const myNotification = new Notify('AI Travel', {
      body,
      lang: 'ja',
      notifyClick: () => {
        window.focus();
      }
    });
    if (!Notify.needsPermission) {
      myNotification.show();
    }
  }, []);

  const handleMessageReceived = useCallback(
    (e: any) => {
      if (serviceId === e.trip.service_id) {
        const receivedMessage = e.user_message;
        let body = `メッセージを受信しました(予約番号：${e.trip.id})\n`;
        body += `内容：${receivedMessage.body}`;
        showDesktopNotification(body);

        $(`#newmark-trip-${e.trip.id}`).fadeIn();
      }
    },
    [serviceId, showDesktopNotification]
  );

  const initWebsocket = useCallback(() => {
    const currentKey = pusherKeyRef.current;
    const currentPrefix = pusherChannelPrefixRef.current;
    const hasChannelPrefix = currentPrefix !== null;
    if (!currentKey || !hasChannelPrefix) {
      const timeoutId = window.setTimeout(initWebsocket, 100);
      timeoutIdsRef.current.push(timeoutId);
      return;
    }
    if (typeof Pusher !== 'undefined') {
      pusherRef.current = new Pusher(currentKey, { encrypted: true });
      eventChannelRef.current = pusherRef.current.subscribe(`${currentPrefix}partner`);
      eventChannelRef.current.bind('message_received', handleMessageReceived);
      createTimeout();
    } else {
      const timeoutId = window.setTimeout(initWebsocket, 100);
      timeoutIdsRef.current.push(timeoutId);
    }
  }, [handleMessageReceived, createTimeout]);

  const initVirtualCounter = useCallback(() => {
    return Fetcher.get<VirtualCounterResponse>('/arrangement/virtual_counter.json').then(result => {
      const newServiceId = parseInt(result.signed_in.arranger.service_id, 10);
      setServiceId(newServiceId);
      setPusherKey(result.pusherKey);
      setPusherChannelPrefix(result.pusherChannelPrefix);
    });
  }, []);

  const fetchSuppliedItems = useCallback(() => {
    return Fetcher.get<SuppliedItemResponseArgs[]>('/arrangement/supplied_items.json?legacy=true').then(result => {
      setSuppliedItems(result.map(raw => new SuppliedItem(convertSuppliedItemResponseToArgs(raw))));
    });
  }, []);

  const addQueryParams = useCallback((tripId?: string | number) => {
    const query: string[] = [];
    if (tripId) {
      query.push(`trip_id=${tripId}`);
    }
    const selectedTripId = location.hash.substr(1);
    const basePath = query.length > 0 ? `${location.pathname}?${query.join('&')}` : location.pathname;
    window.history.pushState({}, '', basePath);
    location.hash = selectedTripId;
  }, []);

  const fetchMessages = useCallback((trip: Trip) => {
    setLoadingMessages(true);
    return Fetcher.get<MessageResponse>(`/trips/${trip.id}/messages.json`, { type: 'arranger' })
      .then(result => {
        setMessages(result.messages);
        setOrderItemStatusLogInfo(result.order_item_status_log_info);
        setLoadingMessages(false);
      })
      .catch(() => {
        setLoadingMessages(false);
      });
  }, []);

  const subscribeTrip = useCallback(
    (tripId: string) => {
      const channelPrefix = pusherChannelPrefixRef.current;
      const hasChannelPrefix = channelPrefix !== null;
      if (!hasChannelPrefix) {
        const timeoutId = window.setTimeout(() => subscribeTrip(tripId), 100);
        timeoutIdsRef.current.push(timeoutId);
        return;
      }
      if (pusherRef.current) {
        if (tripChannelRef.current) {
          pusherRef.current.unsubscribe(tripChannelRef.current.name);
        }
        tripChannelRef.current = pusherRef.current.subscribe(channelPrefix + tripId.toString());
        const throttledReceiveMessage = throttle(() => {
          const latestTrip = selectedTripRef.current;
          if (latestTrip) {
            fetchMessages(latestTrip);
          }
        }, 60000);
        tripChannelRef.current.bind('new_message', throttledReceiveMessage);
        createTimeout();
      } else {
        const timeoutId = window.setTimeout(() => subscribeTrip(tripId), 100);
        timeoutIdsRef.current.push(timeoutId);
      }
    },
    [fetchMessages, createTimeout]
  );

  const reloadSelectedTrip = useCallback(
    (tripId: string): Promise<void> => {
      setEditingOrder(null);
      setLoadingSelectedTrip(true);
      setTripNotFindMessage('');
      if (!tripId) {
        return Promise.resolve();
      }

      return Fetcher.get<TripResponse>(`/arrangement/trips/${tripId}.json`)
        .then(
          result => {
            const showFee = result.trip.user.organization?.show_fee ?? false;
            const trip = new Trip(convertTripResponseToArgs(result.trip, showFee));
            if (!trip) {
              return;
            }

            let editingOrder: Order | null = null;
            if (result.trip.current_order) {
              const orderResponse = {
                ...result.trip.current_order,
                shipping_category_options: result.shipping_category_options,
                wifi_provider_options: result.wifi_provider_options,
                hotel_element_provider_options: result.hotel_element_provider_options,
                car_type_options: result.car_type_options,
                car_provider_options: result.car_provider_options
              };
              editingOrder = new Order(convertOrderResponseToArgs(orderResponse, showFee, trip));
              editingOrder.removeOldFees();
            }

            setLoadingSelectedTrip(false);
            setSelectedTrip(trip);
            selectedTripRef.current = trip;
            setEditingOrder(editingOrder);
            setPlanName(result.organization_plan);
            setTicketOprResponse(result.ticket_opr_response_price ?? []);
            setHotelElementProviderOptions(result.hotel_element_provider_options);
            setShareholderTickets(new ShareholderTicketList(result.shareholder_tickets));
            setMessageTemplates(result.message_templates);
            setBulkTickets((result.bulk_tickets || []).map(t => new BulkTicket(t)));
            setReservationJson({
              fp: result.fp_reservation_json,
              mynavi: result.mynavi_reservation_json
            });
            setTaxTypes(result.tax_types.map(t => new TaxType(t)));
            setPaymentMethods(new PaymentMethodList(result.payment_methods));
            setShareholderInfos(result.shareholder_infos);
            setCabinOptions(result.cabin_options);
            setIndividualTargetSuppliedItems(result.individual_target_supplied_items);
            setIndividualDomesticAirSuppliedItems(result.individual_domestic_air_supplied_items);

            document.title = `手配 - 旅程番号: ${trip.id}`;
            fetchMessages(trip);
            subscribeTrip(trip.id.toString());
            addQueryParams(trip.id.toString());
          },

          error => {
            setLoadingSelectedTrip(false);
            setTripNotFindMessage(
              error instanceof HTTPError && error.response?.status === 400 && error.response.data.errors
            );
            selectedTripRef.current = null;
          }
        )
        .catch(e => {
          console.error(e);
          setLoadingSelectedTrip(false);
          selectedTripRef.current = null;
        });
    },
    [fetchMessages, subscribeTrip, addQueryParams]
  );

  const searchByTripId = useCallback(
    async (tripId: string): Promise<void> => {
      setSelectedTrip(null);
      setTripNotFindMessage('旅程番号を入力してください');

      if (!tripId) {
        addQueryParams();
        return;
      }

      reloadSelectedTrip(tripId);
    },
    [reloadSelectedTrip]
  );

  // Effects
  useEffect(() => {
    appendScript('https://js.pusher.com/3.0/pusher.min.js');
    initVirtualCounter();
    fetchSuppliedItems();

    if (initialTripId) {
      searchByTripId(initialTripId);
    }

    if (Notify.needsPermission && Notify.isSupported()) {
      Notify.requestPermission();
    }

    document.title = '手配';

    return () => {
      createTimeout();
      document.title = '管理画面';
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    pusherKeyRef.current = pusherKey;
  }, [pusherKey]);

  useEffect(() => {
    pusherChannelPrefixRef.current = pusherChannelPrefix;
  }, [pusherChannelPrefix]);

  useEffect(() => {
    if (pusherKey && pusherChannelPrefix !== null) {
      initWebsocket();
    }
  }, [pusherKey, pusherChannelPrefix, initWebsocket]);

  return (
    <VirtualCounterProvider suppliedItems={suppliedItems}>
      <div
        style={{
          fontSize: '14px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1
        }}
      >
        <VirtualCounterHeader
          initialTripId={initialTripId}
          trip={selectedTrip}
          errorMessage={tripNotFindMessage}
          onSubmitSearch={searchByTripId}
        />
        <main style={{ display: 'flex', flexGrow: 1 }}>
          <VirtualCounterBody
            loadingTrip={loadingSelectedTrip}
            loadingMessages={loadingMessages}
            messages={messages}
            messageTemplates={messageTemplates}
            selectedTrip={selectedTrip}
            fetchMessages={fetchMessages}
            serviceId={serviceId}
          />
          {selectedTrip && editingOrder && (
            <HorizontalResizableContainer minWidth={400}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }}
              >
                <div
                  style={{
                    flexBasis: 0,
                    flexGrow: 1,
                    overflowY: 'scroll',
                    overflowX: 'hidden'
                  }}
                >
                  <VirtualCounterAside
                    selectedTrip={selectedTrip}
                    ticketOprResponse={ticketOprResponse}
                    planName={planName}
                    reservationJson={reservationJson}
                    serviceId={serviceId}
                    editingOrder={editingOrder}
                    shareholderTickets={shareholderTickets}
                    bulkTickets={bulkTickets}
                    taxTypes={taxTypes}
                    paymentMethods={paymentMethods}
                    shareholderInfos={shareholderInfos}
                    cabinOptions={cabinOptions}
                    individualTargetSuppliedItems={individualTargetSuppliedItems}
                    individualDomesticAirSuppliedItems={individualDomesticAirSuppliedItems}
                    hotelElementProviderOptions={hotelElementProviderOptions}
                    orderItemStatusLogInfo={orderItemStatusLogInfo}
                    onReloadTrip={() => reloadSelectedTrip(selectedTrip.id.toString())}
                  />
                </div>
              </div>
            </HorizontalResizableContainer>
          )}
        </main>
      </div>
    </VirtualCounterProvider>
  );
};

export default observer(VirtualCounter);
